import { useEffect, useState } from 'react';
import { Box, Alert, LinearProgress, TextField, Button, Typography } from '@mui/material';
import { useAuthTokenContext } from '../context/AuthTokenContext';
import { useQueryParamsContext } from '../context/QueryParamsContext';
import { useTranslation } from '../i18n/translations';
import { useVerifyAndEnroll } from '../hooks/useVerifyAndEnroll';
import appConfigJson from '../config/app-config.json';
import type { AppConfig } from '../types/appConfig';

const appConfig = appConfigJson as AppConfig;

// Eckoh credit card response interface
interface EckohCreditCardResponse {
  message_type: string;
  token: string;
  first_name: string;
  last_name: string;
}

export function EckohCheckout() {
  const { authToken } = useAuthTokenContext();
  const { memberCardNumber, legacyAccountId, countryParam, locationNumber, userId, linkId: urlLinkId } = useQueryParamsContext();
  const { t } = useTranslation();
  const { verify, verifying, verifyResponse, verifyError, resetState } = useVerifyAndEnroll();

  const [linkId, setLinkId] = useState<string>(urlLinkId || '');
  const [eckohUrl, setEckohUrl] = useState<string>('');
  const [manualLinkIdInput, setManualLinkIdInput] = useState<string>('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [tokenizationError, setTokenizationError] = useState<string | null>(null);

  // Normalize country to 3-char code for eckohParamsByCountry lookup
  const normalizeCountry = (c?: string) => {
    const u = c?.toUpperCase() || '';
    if (u === 'US' || u === 'USA') return 'USA';
    if (u === 'CA' || u === 'CAN') return 'CAN';
    return u;
  };

  // Build Eckoh iframe URL
  useEffect(() => {
    if (linkId) {
      const country = normalizeCountry(countryParam);
      const eckohParams =
        (appConfig.eckohParamsByCountry && country && appConfig.eckohParamsByCountry[country]) ||
        appConfig.eckohParams || {
          cId: '(-~2tLW4AqIt*Lb(zO1p2l!r',
          mId: 'eckoh_ttp_sbx',
          allowCvvOption: 'false'
        };

      const params = new URLSearchParams({
        ...eckohParams,
        agentId: linkId
      });

      const baseUrl = appConfig.eckohBaseUrl || 'https://costco.vt.uat.eckoh.uk';
      const finalUrl = `${baseUrl}?${params.toString()}`;
      setEckohUrl(finalUrl);
      console.log('Eckoh iframe URL with linkId:', finalUrl);
    } else {
      setEckohUrl('');
    }
  }, [linkId, countryParam]);

  // Listen for postMessage events from the Eckoh VT iframe
  useEffect(() => {
    async function handleMessage(event: MessageEvent) {
      // Only accept messages from trusted Eckoh iframe origins
      if (
        event.origin !== 'https://costco.vt.uat.eckoh.uk' &&
        event.origin !== 'https://costco.vt.eckoh.us'
      ) {
        return;
      }

      const data = event.data;

      // Handle different message types from VT
      switch (data.message_type) {
        case 'vt_load_complete':
          console.log('VT iframe loaded successfully');
          break;
        
        case 'result':
          const creditCardResult = data as EckohCreditCardResponse;
          console.log('Received token from Eckoh iframe:', creditCardResult);
          
          // Reset any previous errors
          setTokenizationError(null);
          resetState();

          // Call verify-and-enroll using the shared hook
          const result = await verify({
            token: creditCardResult.token,
            tokenIssuer: 'Cybersource',
            tokenType: 'CybersourceFlexTransientToken',
            firstName: creditCardResult.first_name,
            lastName: creditCardResult.last_name,
            country: countryParam?.toUpperCase(),
            memberCardNumber,
            legacyAccountId,
            locationNumber,
            userId
          }, authToken);

          if (result.success) {
            setPaymentSuccess(true);
          }
          break;
        
        case 'error':
          console.error('Payment submission error from Eckoh:', {
            code: data.code,
            message: data.message,
          });
          setTokenizationError(`Payment error: ${data.message || 'Unknown error'}`);
          break;
        
        default:
          console.log('Unknown message type from VT:', data);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [authToken, memberCardNumber, legacyAccountId, countryParam, locationNumber, userId, verify, resetState]);

  const handleManualLinkIdSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualLinkIdInput.trim()) {
      setLinkId(manualLinkIdInput.trim());
      setPaymentSuccess(false);
      resetState();
    }
  };

  return (
    <Box maxWidth={1000} component="section" sx={{ mt: 2, mx: 'auto', px: 2 }}>
      {verifying && <LinearProgress sx={{ mb: 2 }} />}

      {/* Success message */}
      {verifyResponse && paymentSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('paymentMethodAddedSuccessfully')}
        </Alert>
      )}

      {/* Verification errors (payment/auto-renew) */}
      {verifyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {verifyError.split('\n').map((line, idx) => (
            <Box key={idx} component={idx === 0 ? 'div' : 'div'} sx={{ fontSize: idx === 0 ? 'inherit' : '0.85em', opacity: idx === 0 ? 1 : 0.8, mt: idx === 0 ? 0 : 0.5 }}>
              {line}
            </Box>
          ))}
        </Alert>
      )}

      {/* Tokenization errors */}
      {tokenizationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tokenizationError}
        </Alert>
      )}

      {/* Manual linkId input (shown when linkId is missing) */}
      {!linkId && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Link ID is required to load the payment form. Please enter it below.
          </Alert>
          <Box component="form" onSubmit={handleManualLinkIdSubmit} sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              label="Link ID / Agent ID"
              value={manualLinkIdInput}
              onChange={(e) => setManualLinkIdInput(e.target.value)}
              size="small"
              fullWidth
              placeholder="Enter link ID to load payment form"
            />
            <Button type="submit" variant="contained" disabled={!manualLinkIdInput.trim()}>
              Load
            </Button>
          </Box>
        </Box>
      )}

      {/* Eckoh iframe */}
      {eckohUrl && !paymentSuccess && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your card information below:
          </Typography>
          <iframe
            id="eckoh_session"
            src={eckohUrl}
            width="100%"
            height="450"
            allow="payment"
            style={{ 
              border: 'none',
              maxWidth: '900px',
              display: 'block'
            }}
            sandbox="allow-scripts allow-forms allow-same-origin"
            title="Eckoh Payment Form"
          />
        </Box>
      )}

      {/* Reset button for success state */}
      {paymentSuccess && (
        <Box sx={{ mt: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setPaymentSuccess(false);
              resetState();
              setLinkId('');
              setManualLinkIdInput('');
            }}
          >
            Add Another Card
          </Button>
        </Box>
      )}
    </Box>
  );
}
